import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/libs/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-secondary text-primary-foreground ",
        destructive: "bg-destructive text-destructive-foreground ",
        outline:
          "border border-input bg-background hover:bg-foreground hover:text-background",
        secondary: "bg-primary/15 text-foreground ",
        ghost: "hover:bg-gray-100 hover:text-black",
        link: "text-primary underline-offset-4 hover:underline",
        roundedGray:
          "rounded-full bg-gray-600 px-5 text-sm font-medium leading-4 text-gray-100 hover:bg-foreground hover:text-background ",
        roundedOrange:
          "rounded-full border-2 border-verdeoorange bg-background px-5 text-sm font-medium leading-4 text-verdeoorange hover:bg-verdeoorange hover:text-background  ",
        roundedBgOrange:
          "rounded-full border-2 border-transparent bg-verdeoorange px-5 text-sm font-medium leading-4 text-background hover:bg-background hover:border-verdeoorange hover:text-verdeoorange  ",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
